import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthShell } from '@/components/auth/AuthShell';
import { MonoLabel, EditorialField } from '@/components/editorial';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { api, stashApplyScreening } from '@/services/api';
import {
  allowsContinueDespiteMiss,
  askedPrescreenFields,
  evaluateFromDefinition,
  liveSections,
  prescreenFields,
  type DefinitionField,
  type FormDefinitionVersion,
} from '@/lib/formDefinitions';
import { DefinitionGate, useFormDefinition, type FormLocalization } from './FormDefinitionGate';
import { FormLanguageNotice, ShownInEnglish } from './FormLanguageNotice';
import { resolveValidationMessage } from './validationMessages';
import { CHIP_OPTION_LIMIT, NATIVE_SELECT_CLASS } from '@/lib/intakeFields';
import {
  isComplete,
  numericAnswerError,
  numericMax,
  stillToAnswer as stillToAnswerLabels,
} from '@/lib/prescreenAnswers';
import { useLocale } from '@/i18n/useLocale';
import { formatList } from '@/i18n/intl';
import { TRF_CONTACT_EMAIL, TRF_CONTACT_PHONE, TRF_CONTACT_PHONE_TEL } from '@/lib/trfContact';

/**
 * F-6 (#330 / F-038) — pre-eligibility checklist.
 *
 * Gates `/apply` AFTER `InviteRequiredStop` (an invite/code is still required
 * to reach this screen) and BEFORE the authenticated-session confirm + signup
 * form — so nothing downstream (account, draft, documents) exists yet when a
 * family sees this. Answers live in component state while a family is
 * answering (D-6c); see "WHAT #1040 CHANGED HERE" below for the two exits that
 * now carry them further, both of which the screen discloses where they happen.
 *
 * RENDERS FROM DATA (form-builder wave PR-4): the questions, the options, the
 * per-option pass/miss/neutral classification, the miss lines, and every string
 * on both result panels come from the published `prescreen`
 * FormDefinitionVersion — TRF edits them in the builder without a deploy. The
 * one network call is a GET of that definition, which is PUBLIC by necessity:
 * this screen runs before an account exists.
 *
 * HARD STOP (D-FB-3, 2026-08-20 — supersedes D-6b's soft warning): a family
 * whose answers miss the criteria is told this isn't a fit and is NOT offered a
 * way to continue. That is now a SETTING on the definition
 * (`allow_continue_despite_miss`, seeded false), so TRF can reverse the ruling
 * themselves if they ever choose to — the reversibility D-FB-3 promised.
 *
 * ── WHAT #1040 CHANGED HERE ────────────────────────────────────────────────
 *
 * TWO THINGS NOW LEAVE THIS SCREEN, and the header above no longer says "never"
 * about either. D-6c held while the pre-screen was purely advisory; it isn't
 * anymore.
 *
 * 1. #1014 (AP-32) — ON EVERY EXIT, the answers are stashed for
 *    `POST /applications`. That gate is FAIL-CLOSED: it refuses a create that
 *    does not carry an answer to every live required question. This component
 *    is the only place those answers exist, so dropping the stash does not
 *    degrade the gate — it locks every family out of the application. The
 *    stash is sessionStorage in the family's own browser; nothing is sent
 *    until they actually create the application.
 * 2. #3306 (Victor, 2026-10-26) — there is no appeal. A family the miss panel
 *    stops can write to or call TRF; the panel shows both, and nothing on this
 *    screen leaves the browser.
 *
 * The scoring function that produced the seeded classifications now lives in
 * `eligibilityContent.ts`, which is the seed source of record rather than a
 * runtime dependency of this file.
 */

export type EligibilityOutcome = 'passed' | 'skipped';

type Answers = Record<string, string>;

function ChoiceGroup({
  question,
  helper,
  options,
  value,
  onChange,
  shownInEnglish,
}: {
  question: string;
  helper?: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
  /** #2109 R2-B18: is this question's string at the field-relative stage
   *  `slot` (`label`, `helper`, `options.<value>.label`) shown in English
   *  because its translation is stale? */
  shownInEnglish?: (slot: string) => boolean;
}) {
  const { t } = useTranslation('forms');
  const mark = (slot: string) => (shownInEnglish?.(slot) ? <ShownInEnglish /> : null);
  return (
    <div>
      <div className="mb-3">
        <p className="text-body font-medium leading-snug text-stevens-ink">
          {question}
          {mark('label')}
        </p>
        {helper && <p className="mt-1 text-body-sm leading-snug text-stevens-ink-3">{helper}{mark('helper')}</p>}
      </div>
      {/* #1054 — `role="group"` + `aria-pressed`, NOT `radiogroup`/`radio`.
          The radio roles promised the ARIA radio keyboard pattern (arrow keys
          move the selection, the group is one Tab stop) and this component
          implements neither: every pill is its own Tab stop and arrows are
          inert. A screen-reader user told "radio" reaches for keys that do
          nothing. These are toggle buttons, so they say so — the same shape the
          wizard's pills already use (`IntakeFieldInput.tsx`). Fully operable by
          Tab + Space either way; this makes the promise match the behavior. */}
      {options.length > CHIP_OPTION_LIMIT ? (
        // #2462: past three options a pill row wraps into a wall of chips;
        // a native select reads as one question with one answer.
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={question}
          className={NATIVE_SELECT_CLASS}
        >
          <option value="">{t('select.placeholder')}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
              {shownInEnglish?.(`options.${option.value}.label`) ? ` (${t('language.shownInEnglish')})` : ''}
            </option>
          ))}
        </select>
      ) : (
      <div className="flex flex-wrap gap-2" role="group" aria-label={question}>
        {options.map((option) => {
          const selected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(option.value)}
              // #2698 (D-2420): at least 44 px tall on a touch screen.
              className={`rounded-sharp border px-4 py-2 text-left text-body leading-snug transition-colors [@media(pointer:coarse)]:min-h-11 ${
                selected
                  ? 'border-stevens-indigo-solid bg-stevens-indigo-solid text-stevens-indigo-solid-fg'
                  : 'border-stevens-indigo/25 bg-stevens-surface text-stevens-ink hover:border-stevens-indigo/50'
              }`}
            >
              {option.label}
              {mark(`options.${option.value}.label`)}
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
}

function DocumentChecklistPanel({ checklist, mark }: {
  checklist: FormDefinitionVersion['copy']['document_checklist'];
  /** #2109 R2-B18: the stale marker for a `copy.` stage key, if any. */
  mark: (key: string) => React.ReactNode;
}) {
  if (!checklist) return null;
  return (
    <div className="mb-8 rounded-sharp border border-stevens-indigo/20 bg-stevens-indigo/[0.03] p-5">
      <MonoLabel>{checklist.title}</MonoLabel>
      {mark('copy.document_checklist.title')}
      <ul className="mt-3 flex flex-col gap-2">
        {(checklist.items ?? []).map((item: { label: string; detail: string }, i: number) => (
          <li key={item.label} className="text-body leading-snug text-stevens-ink-2">
            <strong className="text-stevens-ink">{item.label}{mark(`copy.document_checklist.items.${i}.label`)}</strong> — {item.detail}{mark(`copy.document_checklist.items.${i}.detail`)}
          </li>
        ))}
      </ul>
      {checklist.footnote && (
        <p className="mt-4 text-body-sm leading-snug text-stevens-ink-3">{checklist.footnote}{mark('copy.document_checklist.footnote')}</p>
      )}
    </div>
  );
}

// `numericMax`, `numericAnswerError`, `isAnswered`, `isComplete` and the
// "Still to answer" rule (`stillToAnswer`) moved to
// `lib/prescreenAnswers.ts` (#3112) so the builder's preview runs the same
// gate this screen does.

/** The line under the disabled check button (2026-10-11, J1 c04). */
const STILL_TO_ANSWER_ID = 'prescreen-still-to-answer';

/**
 * Which of the three screens a family walks (#1039 PR-E, D-CS-12).
 *
 * The Copy tab previews this component — the REAL one, not a facsimile that
 * agrees with it today and drifts next month — inside a phone-sized frame, and
 * a preview cannot click. So it needs to be able to OPEN on any of the screens.
 *
 * One optional prop, and every field in it is an INITIAL STATE rather than a
 * branch: nothing below reads `preview` after mount, so the family paths are
 * byte-for-byte the paths that shipped and a preview genuinely exercises them.
 */
export interface PrescreenPreview {
  screen: 'questions' | 'all_clear' | 'miss';
}

/**
 * Answers that land a family on one result screen or the other, derived from
 * the definition itself.
 *
 * DERIVED, NEVER HARDCODED: the preview then runs the SAME `evaluateFromDefinition`
 * a family's answers run through, so the stop screen shows the real miss lines
 * from the draft being edited rather than a stand-in sentence. Edit an answer's
 * miss line in the Questions tab and it shows up here.
 *
 * A definition where nothing can miss has no stop screen, and asking for one
 * honestly returns the all-clear answers.
 */
export function previewPrescreenAnswers(
  version: FormDefinitionVersion,
  outcome: 'all_clear' | 'miss',
): Answers {
  const fields = prescreenFields(version);
  const answers: Answers = {};

  for (const field of fields) {
    if (field.type === 'number') {
      const range = field.eligible_range;
      answers[field.id] = String(range?.max ?? range?.min ?? 0);
      continue;
    }
    const live = (field.options ?? []).filter((o) => !o.retired);
    answers[field.id] = (live.find((o) => o.eligibility !== 'miss') ?? live[0])?.value ?? '';
  }

  if (outcome === 'miss') {
    // #1639: the miss has to land on a question the seeded answers above
    // actually REVEAL, or `evaluateFromDefinition` skips it and the stop screen
    // the editor asked to see never appears.
    for (const field of askedPrescreenFields(version, answers)) {
      const missing = (field.options ?? []).find((o) => !o.retired && o.eligibility === 'miss');
      if (missing) { answers[field.id] = missing.value; break; }
      const ceiling = field.eligible_range?.max;
      if (field.type === 'number' && typeof ceiling === 'number') {
        answers[field.id] = String(ceiling + 1);
        break;
      }
    }
  }

  return answers;
}

export function EligibilityForm({
  version,
  onComplete,
  preview,
  localization,
  notice,
  codeEntry,
}: {
  version: FormDefinitionVersion;
  onComplete: (outcome: EligibilityOutcome) => void;
  /** #2819: shown above the questions — why a signed-in family is here. */
  notice?: React.ReactNode;
  /** 2026-10-28: the code box, above the questions, so a renewal code can skip them. */
  codeEntry?: React.ReactNode;
  preview?: PrescreenPreview;
  /** What `useFormDefinition` reported about the language (#2109 R2). The
   *  copy editor's preview passes none: English, no language lines. */
  localization?: FormLocalization;
}) {
  const { locale: uiLocale } = useLocale();
  const { i18n } = useTranslation('validation');
  // #2109 R3 L8 (#2133): the /apply chrome around the definition's own words
  // (document label, time estimate, footer, age unit, stop-screen label).
  const { t: tf } = useTranslation('forms');
  const staleKeys = new Set(localization?.staleFallbacks ?? []);
  /** #2109 R2-B18: the per-string marker for a stale stage key — the only
   *  per-string language note. `c` is the same for a `copy.` leaf. */
  const m = (key: string) => (staleKeys.has(key) ? <ShownInEnglish /> : null);
  const c = (path: string) => m(`copy.${path}`);
  const [answers, setAnswers] = useState<Answers>(
    () => (preview && preview.screen !== 'questions'
      ? previewPrescreenAnswers(version, preview.screen)
      : {}),
  );
  const [submitted, setSubmitted] = useState(() => Boolean(preview) && preview!.screen !== 'questions');
  // Which numeric boxes the family has been INTO. An empty box is an error the
  // moment they leave it empty, but it is not an error before they have typed
  // anything — a form that opens shouting at you is a form nobody trusts.
  // Out-of-range needs no such grace: those digits are already wrong.
  const [visited, setVisited] = useState<Record<string, true>>({});

  /**
   * #1053 — where a screen reader lands when this screen changes underneath it.
   *
   * Submitting UNMOUNTS the form, so whatever had focus goes with it and
   * `document.activeElement` falls back to `<body>`: the verdict — including
   * the miss panel's humane copy, the most consequential paragraph in the whole
   * flow — was announced to nobody. Same on the way back, when "Review my
   * answers" unmounts the panel.
   *
   * Focus moves to the panel's `<h1>` rather than a `role="status"` region: a
   * live region reads one sentence and leaves the reader parked on a button
   * that no longer exists, where focus takes them TO the panel with everything
   * under the heading in reading order. Exactly one of the three `<h1>`s is
   * mounted at a time, so they share the ref.
   *
   * NOT on first paint. The previous-value guard is what keeps this from
   * yanking focus out of the page on arrival — including in the Copy tab's
   * preview, which opens directly on a result screen.
   *
   * #1321 — a boolean "have I painted before" ref does NOT survive
   * `<StrictMode>`: React double-invokes this effect on mount with no
   * cleanup in between, so the ref flips true on the first invocation and
   * the second invocation reads it as "already painted" and steals focus
   * on arrival. Comparing against the PREVIOUS VALUE of `submitted` instead
   * is idempotent under the double-invoke — both invocations see the same
   * `submitted`, so neither looks like a change, and a real change is still
   * detected the moment it happens.
   */
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const previousSubmitted = useRef(submitted);
  useEffect(() => {
    if (previousSubmitted.current !== submitted) {
      headingRef.current?.focus();
    }
    previousSubmitted.current = submitted;
  }, [submitted]);

  // #1639: the questions this family is being ASKED, not every question the
  // definition lists. ONE list feeds the render, the submit gate and the stash,
  // and it is the same `isRevealed` rule the wizard, the builder preview and
  // the server's own store gate read — the defect this replaces was four
  // consumers honouring `visible_when` and this one not.
  const fields = askedPrescreenFields(version, answers);
  const sections = liveSections(version);
  const complete = isComplete(fields, answers);
  // 2026-10-11 (J1 c04): the button stayed disabled with every answer but the
  // age given, and nothing said which was missing. Once the family has
  // answered anything, the questions still open are named under the button.
  // The rule lives in lib/prescreenAnswers.ts so the builder preview names
  // the same questions (#3112).
  const stillToAnswer = stillToAnswerLabels(fields, answers);
  const misses = evaluateFromDefinition(version, answers);
  const copy = version.copy ?? {};

  const setField = (id: string, value: string) => setAnswers((prev) => ({ ...prev, [id]: value }));

  /** The error a numeric box should be SHOWING right now, if any. */
  const shownNumericError = (field: DefinitionField): string | undefined => {
    const raw = answers[field.id] ?? '';
    const problem = numericAnswerError(field, raw);
    if (!problem) return undefined;
    if (raw.trim() === '' && !visited[field.id]) return undefined;
    return resolveValidationMessage(i18n, uiLocale, { key: 'number.wholeBetween', params: problem });
  };

  /**
   * #1014 (AP-32) — EVERY exit from this screen runs through here.
   *
   * `POST /applications` is fail-closed: it refuses a create that does not
   * carry an answer to every live required pre-screen question. These answers
   * exist nowhere else, and `onComplete` takes the family off this screen, so
   * the stash has to happen FIRST and on every path that leads to the form —
   * the all-clear CTA, the "I'll come back" exit (which unblocks the signup
   * form just the same), and the continue-past-miss path if TRF ever flips
   * D-FB-3 back on. Miss one and that family reaches a 422 they cannot fix.
   */
  const finish = (outcome: EligibilityOutcome) => {
    stashApplyScreening(answers);
    onComplete(outcome);
  };

  const shell = (children: React.ReactNode) => (
    <AuthShell
      documentLabel={tf('applyChrome.applyToTrf')}
      meta={[{ k: tf('applyChrome.estTime'), v: tf('prescreenChrome.estTimeValue') }]}
    >
      {children}
      <p className="mt-10 text-caption text-stevens-ink-2/60">
        {tf('applyChrome.footer')}
      </p>
    </AuthShell>
  );

  if (!submitted) {
    const heading = copy.heading ?? {};
    return shell(
      <>
        {notice && (
          <p role="status" data-testid="eligibility-notice" className="mb-6 border-l-2 border-stevens-indigo/40 pl-4 text-body leading-snug text-stevens-ink">
            {notice}
          </p>
        )}
        <div className="mb-5">
          <MonoLabel>{heading.eyebrow}</MonoLabel>
          {c('heading.eyebrow')}
        </div>

        {/* #1053 — the focus target on the way BACK from a result panel. The
            default focus ring is left on deliberately: the focus moves without
            the family asking, so a sighted low-vision or magnifier user needs
            to be able to see where it went. */}
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="font-serif text-[clamp(28px,3.6vw,40px)] leading-[1.15] tracking-tight text-stevens-ink mb-4"
        >
          {heading.title}
          {c('heading.title')}
        </h1>

        <p className="text-lede leading-relaxed text-stevens-ink-2 mb-8 max-w-[460px]">
          {heading.lede}
          {c('heading.lede')}
        </p>

        {localization && <FormLanguageNotice localization={localization} className="-mt-4 mb-8 space-y-1" />}

        {codeEntry}

        <form
          className="flex flex-col gap-8"
          onSubmit={(e) => {
            e.preventDefault();
            if (!complete) return;
            setSubmitted(true);
          }}
          noValidate
        >
          {sections.map((section) => {
            const sectionFields = fields.filter((f) => f.section_id === section.id);
            if (!sectionFields.length) return null;
            return (
              <div key={section.id} className="border-l-2 border-stevens-indigo/20 pl-5 flex flex-col gap-6">
                {/* Grammar v3.3 (2026-08-27): this heads a group of questions
                    the family ANSWERS, so it is a heading — card-title Fraunces
                    on the ink rung — not an eyebrow on the 11px label rung. */}
                <h2 className="text-card-title font-serif text-stevens-ink">{section.title}{m(`section.${section.id}.title`)}</h2>
                {sectionFields.map((field) =>
                  field.type === 'number' ? (
                    /* A two-digit answer gets a two-digit box (design grammar:
                       a box's width is a claim about how much data belongs in
                       it). `type="text"` + inputMode/pattern is what gets a
                       digit keypad on mobile while leaving the value ours to
                       validate — see `numericAnswerError` for why the range
                       cannot live on the input. The unit is the one display
                       string not derivable from the definition; the prescreen
                       has exactly one numeric question and it is an age, so a
                       second one would need a unit slot on the field (?). */
                    <EditorialField
                      key={field.id}
                      label={field.label}
                      id={`eligibility-${field.id}`}
                      name={field.id}
                      width="numeric"
                      unit={tf('prescreenChrome.ageUnit')}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={String(numericMax(field)).length}
                      value={answers[field.id] ?? ''}
                      onChange={(e) => {
                        setVisited((prev) => ({ ...prev, [field.id]: true }));
                        // #2690: what the family typed is kept as typed. Stripping
                        // non-digits turned "1.5" into 15 and "8.9" into 89 with no
                        // word said; `numericAnswerError` refuses it with the range
                        // message instead, and `maxLength` still bounds the length.
                        setField(field.id, e.target.value);
                      }}
                      onBlur={() => setVisited((prev) => ({ ...prev, [field.id]: true }))}
                      error={shownNumericError(field)}
                      trailing={m(`field.${field.section_id}.${field.id}.label`) ?? undefined}
                    />
                  ) : (
                    <ChoiceGroup
                      key={field.id}
                      question={field.label}
                      helper={field.helper}
                      options={(field.options ?? []).filter((o) => !o.retired)}
                      value={answers[field.id] ?? ''}
                      onChange={(value) => setField(field.id, value)}
                      shownInEnglish={(slot) => staleKeys.has(`field.${field.section_id}.${field.id}.${slot}`)}
                    />
                  ),
                )}
              </div>
            );
          })}

          <button
            type="submit"
            disabled={!complete}
            aria-describedby={stillToAnswer.length > 0 ? STILL_TO_ANSWER_ID : undefined}
            className="flex items-center justify-center rounded-sharp bg-stevens-indigo-solid px-6 py-3 text-body font-medium text-stevens-indigo-solid-fg transition-colors hover:bg-stevens-indigo-solid-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {heading.submitCta}
            {c('heading.submitCta')}
          </button>
          {stillToAnswer.length > 0 && (
            <p id={STILL_TO_ANSWER_ID} data-testid={STILL_TO_ANSWER_ID} className="-mt-2 text-body-sm text-stevens-ink-3" aria-live="polite">
              {tf('prescreenChrome.stillToAnswer', { items: formatList(stillToAnswer, { type: 'conjunction' }) })}
            </p>
          )}
        </form>
      </>,
    );
  }

  if (misses.length === 0) {
    const clear = copy.result_all_clear ?? {};
    return shell(
      <>
        <div className="mb-5">
          <MonoLabel>{clear.eyebrow}</MonoLabel>
          {c('result_all_clear.eyebrow')}
        </div>

        {/* #1053 — focus lands here when the form is replaced by the verdict. */}
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="font-serif text-[clamp(28px,3.6vw,40px)] leading-[1.15] tracking-tight text-stevens-ink mb-4"
        >
          {clear.title}
          {c('result_all_clear.title')}
        </h1>

        <p className="text-lede leading-relaxed text-stevens-ink-2 mb-8 max-w-[460px]">{clear.lede}{c('result_all_clear.lede')}</p>

        <DocumentChecklistPanel checklist={copy.document_checklist} mark={m} />

        {/* "Disclose at the act" (Mario, 2026-08-22): the answers above ride
            along to the application, and the family is told so HERE, next to
            the button that does it — not only in the lede. A seeded slot, so
            TRF can revise the sentence without a deploy. */}
        {clear.saveNotice && (
          <p className="mb-5 text-body-sm leading-snug text-stevens-ink-3 max-w-[460px]">
            {clear.saveNotice}
            {c('result_all_clear.saveNotice')}
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => finish('passed')}
            className="flex items-center justify-center rounded-sharp bg-stevens-indigo-solid px-6 py-3 text-body font-medium text-stevens-indigo-solid-fg transition-colors hover:bg-stevens-indigo-solid-2"
          >
            {clear.startCta}
            {c('result_all_clear.startCta')}
          </button>
          <button
            type="button"
            onClick={() => finish('skipped')}
            className="flex items-center justify-center rounded-sharp border border-stevens-indigo/30 px-6 py-3 text-body font-medium text-stevens-indigo transition-colors hover:bg-stevens-indigo/5"
          >
            {clear.skipCta}
            {c('result_all_clear.skipCta')}
          </button>
        </div>
      </>,
    );
  }

  const miss = copy.result_miss ?? {};
  // D-FB-3: false on the seeded version — the hard stop. TRF can flip this in
  // the builder without a deploy, which is the reversibility the ruling
  // promised; until they do, there is deliberately no way forward from here.
  const mayContinue = allowsContinueDespiteMiss(version);
  /** #2109 R2-B18: a miss line is the range's (a number) or the chosen
   *  option's; mark it when that slot's translation is stale. */
  const missLineMark = (fieldId: string) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return null;
    const base = `field.${field.section_id}.${field.id}`;
    return field.type === 'number'
      ? m(`${base}.eligible_range.miss_line`)
      : m(`${base}.options.${answers[field.id] ?? ''}.miss_line`);
  };

  return shell(
    <>
      <div className="mb-5">
        <MonoLabel>{miss.eyebrow}</MonoLabel>
        {c('result_miss.eyebrow')}
      </div>

      {/* #1053 — the stop screen is the one that most needs announcing, and it
          was the one announced least: the humane closing and the open door sit
          under this heading, in reading order, once focus arrives. */}
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="font-serif text-[clamp(28px,3.6vw,40px)] leading-[1.15] tracking-tight text-stevens-ink mb-4"
      >
        {miss.title}
        {c('result_miss.title')}
      </h1>

      <p className="text-lede leading-relaxed text-stevens-ink-2 mb-3 max-w-[460px]">{miss.intro}{c('result_miss.intro')}</p>

      <ul className="mb-5 flex flex-col gap-2 border-l-2 border-stevens-indigo/20 pl-5">
        {misses.map((entry) => (
          <li key={entry.fieldId} className="text-body leading-snug text-stevens-ink-2">
            {entry.line}
            {missLineMark(entry.fieldId)}
          </li>
        ))}
      </ul>

      {/* The protected stop-screen copy. It ships as the SEED VALUE of an
          editable slot (spec §Guardrails): verbatim on day one, TRF's to revise
          thereafter — and the contract suite carves it out of the locked set
          explicitly so nobody "protects" it back into code. */}
      <p className="text-lede leading-relaxed text-stevens-ink-2 mb-4 max-w-[460px]">{miss.closing}{c('result_miss.closing')}</p>
      <p className="text-lede leading-relaxed text-stevens-ink-2 mb-4 max-w-[460px]">{miss.openDoor}{c('result_miss.openDoor')}</p>

      {/* #3306 (Victor, 2026-10-26): the appeal is gone. The open door above
          says "get in touch"; this is where. TRF's own published contact
          details (trf.org/contact-us), not form copy, so they are not a slot.
          The colour and underline sit on the inner span because
          `.landing-page a` (index.css) resets every anchor on this shell and
          outranks a utility on the anchor itself; the anchor keeps the 44px
          coarse-pointer target the landing page's links use. */}
      <dl className="mb-8 flex flex-col gap-1 text-body leading-snug text-stevens-ink-2">
        <div className="flex gap-2">
          <dt className="text-stevens-ink-3">{tf('prescreenChrome.contactEmail')}</dt>
          <dd>
            <a href={`mailto:${TRF_CONTACT_EMAIL}`} className="rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:-my-3 [@media(pointer:coarse)]:inline-flex [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:items-center">
              <span className="text-stevens-indigo underline underline-offset-4 hover:text-stevens-indigo-2">{TRF_CONTACT_EMAIL}</span>
            </a>
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-stevens-ink-3">{tf('prescreenChrome.contactPhone')}</dt>
          <dd>
            <a href={`tel:${TRF_CONTACT_PHONE_TEL}`} className="rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:-my-3 [@media(pointer:coarse)]:inline-flex [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:items-center">
              <span className="text-stevens-indigo underline underline-offset-4 hover:text-stevens-indigo-2">{TRF_CONTACT_PHONE}</span>
            </a>
          </dd>
        </div>
      </dl>

      <div className="mb-8 flex flex-col gap-3 sm:flex-row">
        {copy.resource_url && (
          <ExternalLink
            href={copy.resource_url}
            className="flex items-center justify-center rounded-sharp bg-stevens-indigo-solid px-6 py-3 text-body font-medium text-stevens-indigo-solid-fg transition-colors hover:bg-stevens-indigo-solid-2"
          >
            {miss.resourceCta}
            {c('result_miss.resourceCta')}
          </ExternalLink>
        )}
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="flex items-center justify-center rounded-sharp border border-stevens-indigo/30 px-6 py-3 text-body font-medium text-stevens-indigo transition-colors hover:bg-stevens-indigo/5"
        >
          {miss.reviewCta}
          {c('result_miss.reviewCta')}
        </button>
        {mayContinue && (
          <button
            type="button"
            onClick={() => finish('passed')}
            className="flex items-center justify-center rounded-sharp border border-stevens-indigo/30 px-6 py-3 text-body font-medium text-stevens-indigo transition-colors hover:bg-stevens-indigo/5"
          >
            {miss.continueCta ?? tf('prescreenChrome.continueAnyway')}
            {c('result_miss.continueCta')}
          </button>
        )}
      </div>

      {/* #2699: a family just told "not a fit" is not handed a list of
          documents to gather. The list shows only when this screen can still
          lead on to the application. */}
      {mayContinue && (
        <>
          <p className="mb-3">
            <MonoLabel>{tf('prescreenChrome.whenReady')}</MonoLabel>
          </p>
          <DocumentChecklistPanel checklist={copy.document_checklist} mark={m} />
        </>
      )}
    </>,
  );
}

export function EligibilityCheck({
  onComplete,
  notice,
  codeEntry,
}: {
  onComplete: (outcome: EligibilityOutcome) => void;
  /** #2819: a line above the questions saying why the family is here. */
  notice?: React.ReactNode;
  /** 2026-10-28: the code box, above the questions. */
  codeEntry?: React.ReactNode;
}) {
  const state = useFormDefinition(api.forms.publishedPrescreen, 'prescreen');

  return (
    <AuthShellIfGated state={state}>
      <DefinitionGate state={state}>
        {(version, form) => (
          <EligibilityForm
            version={version}
            onComplete={onComplete}
            localization={form.localization}
            notice={notice}
            codeEntry={codeEntry}
          />
        )}
      </DefinitionGate>
    </AuthShellIfGated>
  );
}

/**
 * Loading and error states need the page chrome too — otherwise a failed fetch
 * renders a bare error on a blank page. Once the definition is ready,
 * `EligibilityForm` brings its own shell, so this steps out of the way rather
 * than nesting two.
 */
function AuthShellIfGated({
  state,
  children,
}: {
  state: { status: string };
  children: React.ReactNode;
}) {
  const { t } = useTranslation('forms');
  if (state.status === 'ready') return <>{children}</>;
  return (
    <AuthShell
      documentLabel={t('applyChrome.applyToTrf')}
      meta={[{ k: t('applyChrome.estTime'), v: t('prescreenChrome.estTimeValue') }]}
    >
      <div className="mb-5">
        <MonoLabel>{t('prescreenChrome.beforeYouBegin')}</MonoLabel>
      </div>
      {children}
    </AuthShell>
  );
}
